import './App.css';
import { SYSTEM_PROMPTS } from './prompts';
import { useEffect, useMemo, useRef, useState } from 'react';

const IMAGE_MAX_PX = 1024;
const IMAGE_QUALITY = 0.8;

function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        if (width > IMAGE_MAX_PX || height > IMAGE_MAX_PX) {
          if (width >= height) {
            height = Math.round((height * IMAGE_MAX_PX) / width);
            width = IMAGE_MAX_PX;
          } else {
            width = Math.round((width * IMAGE_MAX_PX) / height);
            height = IMAGE_MAX_PX;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', IMAGE_QUALITY);
        resolve({
          base64: dataUrl.split(',')[1],
          mediaType: 'image/jpeg',
          previewUrl: dataUrl,
          fileName: file.name || 'imagen adjunta',
        });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}


const PROFILE_OPTIONS = [
  { value: 'primariaMedia', label: 'Primaria Media (3° – 4°)' },
  { value: 'primariaAlta', label: 'Primaria Alta (5° – 6°)' },
  { value: 'preparatoria', label: 'Preparatoria (1° – 3°)' },
];

const PROFILE_META = {
  primariaMedia: {
    shortLabel: 'Primaria Media',
    studentLabel: 'Niño',
    avatarSrc: '/assets/avatar-primaria.png',
    studentClass: 'primary',
  },
  primariaAlta: {
    shortLabel: 'Primaria Alta',
    studentLabel: 'Estudiante',
    avatarSrc: '/assets/avatar-secundaria.png',
    studentClass: 'secondary',
  },
  preparatoria: {
    shortLabel: 'Preparatoria',
    studentLabel: 'Joven',
    avatarSrc: '/assets/avatar-prepa.png',
    studentClass: 'high',
  },
};

const GUARDRAILS = [
  { label: 'Perfil por edad', type: 'profile' },
  { label: 'Imagen habilitada', type: 'image' },
  { label: 'Idioma de la tarea', type: 'language' },
  { label: 'Guía sin resolver', type: 'book' },
];
const CONVERSATION_HOLD_KEY = 'lumenConversationHoldUntil';
const CONVERSATION_HOLD_MS = 12 * 60 * 60 * 1000;

function readConversationHoldUntil() {
  try {
    const stored = window.localStorage.getItem(CONVERSATION_HOLD_KEY);
    const value = Number(stored);
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

function storeConversationHoldUntil(value) {
  try {
    if (value > Date.now()) {
      window.localStorage.setItem(CONVERSATION_HOLD_KEY, String(value));
    } else {
      window.localStorage.removeItem(CONVERSATION_HOLD_KEY);
    }
  } catch {
    // Storage can be unavailable in private or test environments.
  }
}

function linkifyPlainText(text, keyPrefix) {
  const urlPattern = /((?:https?:\/\/|www\.)[^\s<>()]+|(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<>()]*)?)/gi;
  const parts = String(text || '').split(urlPattern);

  return parts.map((part, index) => {
    const key = `${keyPrefix}-link-${index}`;
    if (!part) return null;
    if (urlPattern.test(part)) {
      urlPattern.lastIndex = 0;
      const trimmed = part.replace(/[.,;:!?]+$/, '');
      const trailing = part.slice(trimmed.length);
      const href = /^https?:\/\//i.test(trimmed)
        ? trimmed
        : `https://${trimmed.replace(/^www\./i, 'www.')}`;
      return (
        <span key={key}>
          <a href={href} target="_blank" rel="noreferrer" className="message-link">
            {trimmed}
          </a>
          {trailing}
        </span>
      );
    }
    urlPattern.lastIndex = 0;
    return <span key={key}>{part}</span>;
  });
}

function renderInlineFormatting(text, keyPrefix) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);

  return parts.map((part, index) => {
    const key = `${keyPrefix}-${index}`;
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={key}>{linkifyPlainText(part.slice(2, -2), key)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={key}>{linkifyPlainText(part.slice(1, -1), key)}</em>;
    }
    return <span key={key}>{linkifyPlainText(part, key)}</span>;
  });
}

function FormattedMessage({ text }) {
  const lines = String(text || '').split('\n');

  return (
    <p className="message-text">
      {lines.map((line, index) => (
        <span key={`line-${index}`}>
          {renderInlineFormatting(line, `line-${index}`)}
          {index < lines.length - 1 && <br />}
        </span>
      ))}
    </p>
  );
}

function ProfileIcon({ profile }) {
  if (profile === 'preparatoria') {
    return (
      <svg viewBox="0 0 40 40" aria-hidden="true">
        <path d="M11 29h18" />
        <path d="M14 29V12h12v17" />
        <path d="M17 16h6" />
        <path d="M17 21h6" />
        <path d="M20 12V8" />
      </svg>
    );
  }

  if (profile === 'primariaAlta') {
    return (
      <svg viewBox="0 0 40 40" aria-hidden="true">
        <path d="M12 30V14a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v16" />
        <path d="M12 17h16" />
        <path d="M16 23h8" />
        <path d="M16 30v-4" />
        <path d="M24 30v-4" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 40 40" aria-hidden="true">
      <path d="M12 30V16a5 5 0 0 1 5-5h6a5 5 0 0 1 5 5v14" />
      <path d="M15 18h10" />
      <path d="M16 11v-2a4 4 0 0 1 8 0v2" />
      <path d="M17 24h6" />
    </svg>
  );
}

function GuardrailIcon({ type }) {
  if (type === 'image') {
    return (
      <svg viewBox="0 0 32 32" aria-hidden="true">
        <rect x="7" y="8" width="18" height="16" rx="2" />
        <path d="m9.5 21 5.2-5.1 3.4 3.4 2.3-2.3 4.1 4" />
        <circle cx="20.5" cy="12.5" r="1.5" />
      </svg>
    );
  }

  if (type === 'language') {
    return (
      <svg viewBox="0 0 32 32" aria-hidden="true">
        <path d="M7 10h11" />
        <path d="M12.5 7v3" />
        <path d="M15.5 10c-1 5.2-4 8.6-8.5 10.5" />
        <path d="M9.5 14c1.3 2.4 3 4.2 5.2 5.4" />
        <path d="M19 24l4-10 4 10" />
        <path d="M20.4 21h5.2" />
      </svg>
    );
  }

  if (type === 'book') {
    return (
      <svg viewBox="0 0 32 32" aria-hidden="true">
        <path d="M6 9.5c3.5-.9 6.8-.4 10 1.6v14c-3.2-2-6.5-2.5-10-1.6v-14Z" />
        <path d="M26 9.5c-3.5-.9-6.8-.4-10 1.6v14c3.2-2 6.5-2.5 10-1.6v-14Z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <circle cx="21" cy="13" r="3" />
      <path d="M5 24c1.2-5 4-7.5 7-7.5s5.8 2.5 7 7.5" />
      <path d="M17.8 19.2c1.1-1.4 2.4-2 4-2 2.5 0 4.6 2.2 5.5 6.3" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      <path d="M16 5 25 8v7.2c0 5.5-3.8 9.6-9 11.8-5.2-2.2-9-6.3-9-11.8V8l9-3Z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      <circle cx="16" cy="16" r="11" />
      <path d="m10.5 16.2 3.6 3.6 7.4-8" />
    </svg>
  );
}

function StudentAvatar({ profile }) {
  const studentClass = PROFILE_META[profile]?.studentClass || 'primary';

  return (
    <span className={`student-avatar ${studentClass}`} aria-label="Avatar del alumno" role="img">
      <svg viewBox="0 0 40 40" aria-hidden="true">
        <path className="student-avatar__shirt" d="M9 35c1.7-6.9 6-10.4 11-10.4S29.3 28.1 31 35" />
        <circle className="student-avatar__face" cx="20" cy="17" r="8.2" />
        <path className="student-avatar__ear" d="M11.8 18.2c-1.3-.1-2.2.7-2.2 1.9 0 1.4 1 2.2 2.4 2.2" />
        <path className="student-avatar__ear" d="M28.2 18.2c1.3-.1 2.2.7 2.2 1.9 0 1.4-1 2.2-2.4 2.2" />
        <path className="student-avatar__hair" d="M12.6 15.3c1.1-5.5 6.9-8.3 12-5.7 2.5 1.3 3.6 3.4 3.1 6.7-3.1-.1-5.5-1.4-7.6-3.5-1.9 2.6-4.3 3.8-7.5 2.5Z" />
        <circle className="student-avatar__eye" cx="16.8" cy="17.8" r="0.9" />
        <circle className="student-avatar__eye" cx="23.2" cy="17.8" r="0.9" />
        <path className="student-avatar__cheek" d="M14 20.6h1.6" />
        <path className="student-avatar__cheek" d="M24.4 20.6H26" />
        <path className="student-avatar__smile" d="M17.4 21.8c1.6 1.4 3.6 1.4 5.2 0" />
      </svg>
    </span>
  );
}

function App() {
  const [selectedProfile, setSelectedProfile] = useState('primariaMedia');
  const [showDebug, setShowDebug] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [attachedImage, setAttachedImage] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(() => {
    // sessionStorage persiste dentro del mismo tab (incluso si recarga la página),
    // pero cada tab nuevo genera su propio ID — un alumno por tab, sin mezclar.
    const stored = sessionStorage.getItem('luxia_session_id');
    if (stored) return stored;
    const newId = crypto.randomUUID();
    sessionStorage.setItem('luxia_session_id', newId);
    return newId;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [conversationHoldUntil, setConversationHoldUntil] = useState(readConversationHoldUntil);
  const composerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const imageInputRef = useRef(null);
  const isConversationLocked = conversationHoldUntil > Date.now();
  const activeProfile = PROFILE_META[selectedProfile];

  const focusComposer = () => {
    const schedule = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
    schedule(() => composerRef.current?.focus());
  };

  const systemPrompt = useMemo(() => {
    return SYSTEM_PROMPTS[selectedProfile];
  }, [selectedProfile]);

  const resetConversation = () => {
    // Nueva conversación = nuevo ID de sesión, también actualiza sessionStorage
    const newId = crypto.randomUUID();
    sessionStorage.setItem('luxia_session_id', newId);
    setSessionId(newId);
    setMessages([]);
    setInputMessage('');
    setAttachedImage(null);
    setErrorMessage('');
  };

  const handleNewConversation = () => {
    resetConversation();
    setConversationHoldUntil(0);
    storeConversationHoldUntil(0);
    focusComposer();
  };

  const handleImageSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      setAttachedImage(compressed);
      focusComposer();
    } catch {
      setErrorMessage('No se pudo cargar la imagen. Intenta con otra.');
    }
  };

  const handleProfileChange = (event) => {
    setSelectedProfile(event.target.value);
    resetConversation();
    focusComposer();
  };

  useEffect(() => {
    const schedule = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
    schedule(() => composerRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!composerRef.current) return;
    composerRef.current.style.height = 'auto';
    composerRef.current.style.height = `${Math.min(composerRef.current.scrollHeight, 220)}px`;
  }, [inputMessage]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setShowDebug((prev) => !prev);
      }
      if (e.ctrlKey && e.shiftKey && e.key === 'R') {
        e.preventDefault();
        setMessages([]);
        setInputMessage('');
        setAttachedImage(null);
        setErrorMessage('');
        setConversationHoldUntil(0);
        storeConversationHoldUntil(0);
        const schedule = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
        schedule(() => composerRef.current?.focus());
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (!conversationHoldUntil) return undefined;

    const remainingMs = conversationHoldUntil - Date.now();
    if (remainingMs <= 0) {
      setConversationHoldUntil(0);
      storeConversationHoldUntil(0);
      resetConversation();
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setConversationHoldUntil(0);
      storeConversationHoldUntil(0);
      resetConversation();
    }, remainingMs);

    return () => window.clearTimeout(timeoutId);
  }, [conversationHoldUntil]);

  const buildAnthropicMessages = (nextMessages) =>
    nextMessages
      .filter((message) => message.role === 'user' || message.role === 'assistant')
      .map((message) => {
        if (message.imageData) {
          const imageTextBlocks = [
            {
              type: 'text',
              text: `[[METADATO_INTERNO_ARCHIVO nombre_original="${String(message.imageData.fileName || 'imagen adjunta').replace(/["\\]/g, '')}"]]`,
            },
            ...(message.content ? [{ type: 'text', text: message.content }] : []),
          ];

          return {
            role: message.role,
            content: [
              {
                type: 'image',
                source: {
                  type: 'base64',
                  media_type: message.imageData.mediaType,
                  data: message.imageData.base64,
                },
              },
              ...imageTextBlocks,
            ],
          };
        }
        return { role: message.role, content: message.content };
      });

  const extractAssistantText = (contentBlocks) => {
    if (!Array.isArray(contentBlocks)) {
      return 'No se recibió una respuesta válida del sistema.';
    }

    const text = contentBlocks
      .filter((block) => block.type === 'text' && typeof block.text === 'string')
      .map((block) => block.text)
      .join('\n')
      .trim();

    return text || 'No se recibió texto en la respuesta del sistema.';
  };

  const sendUserMessage = async ({ text, imageData }) => {
    const trimmedMessage = String(text || '').trim();
    if ((!trimmedMessage && !imageData) || isLoading) {
      return;
    }

    if (isConversationLocked) {
      setErrorMessage('La conversación quedó cerrada por hoy. Puedes volver a practicar en 12 horas con una conversación nueva.');
      return;
    }

    const userMessage = {
      role: 'user',
      content: trimmedMessage,
      imageData: imageData || undefined,
    };
    const updatedMessages = [...messages, userMessage];

    setMessages(updatedMessages);
    setInputMessage('');
    setAttachedImage(null);
    setErrorMessage('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 512,
          profile: selectedProfile,
          system: systemPrompt,
          sessionId,
          messages: buildAnthropicMessages(updatedMessages),
        }),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`Error ${response.status}: ${errorBody}`);
      }

      const data = await response.json();
      const assistantText = extractAssistantText(data.content);
      const debug = data.debug || null;
      const quickActions = Array.isArray(data.quickActions) ? data.quickActions : [];

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: assistantText, debug, quickActions },
      ]);
      if (debug?.directive === 'directed_profanity_closure') {
        const holdUntil = Date.now() + CONVERSATION_HOLD_MS;
        setConversationHoldUntil(holdUntil);
        storeConversationHoldUntil(holdUntil);
      }
    } catch (error) {
      const friendlyError =
        error instanceof Error
          ? error.message
          : 'Ocurrió un error inesperado al contactar la API.';
      setErrorMessage(friendlyError);
    } finally {
      setIsLoading(false);
      focusComposer();
    }
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    await sendUserMessage({ text: inputMessage, imageData: attachedImage });
  };

  const handleQuickAction = async (action) => {
    await sendUserMessage({ text: action?.value || action?.label || '', imageData: null });
  };

  const handleComposerKeyDown = (event) => {
    const isComposing =
      event.isComposing ||
      event.nativeEvent?.isComposing ||
      event.keyCode === 229;

    if (isComposing || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
  };

  return (
    <div className="app-shell">
      <aside className="sidebar" aria-label="Navegación principal">
        <div className="brand-lockup">
          <h1 className="visually-hidden">LuxIA</h1>
          <img src="/assets/luxia_logo.png" alt="LuxIA by Nodo" className="brand-logo" />
        </div>
        <nav className="primary-nav" aria-label="Secciones">
          <a href="#chat" className="nav-item active">
            <span aria-hidden="true">✦</span>
            Chat
          </a>
          <a href="#historial" className="nav-item">
            <span aria-hidden="true">↺</span>
            Historial
          </a>
          <a href="/panel-supervision.html" className="nav-item">
            <span aria-hidden="true">⚙</span>
            Ajustes
          </a>
        </nav>
        <button
          type="button"
          className="hamburger-button"
          onClick={() => setShowMobileNav((prev) => !prev)}
          aria-label="Menú de navegación"
          aria-expanded={showMobileNav}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>
        {showMobileNav && (
          <div className="mobile-nav-menu" role="menu">
            <a href="#chat" className="mobile-nav-item" role="menuitem" onClick={() => setShowMobileNav(false)}>
              <span aria-hidden="true">✦</span>
              Chat
            </a>
            <a href="#historial" className="mobile-nav-item" role="menuitem" onClick={() => setShowMobileNav(false)}>
              <span aria-hidden="true">↺</span>
              Historial
            </a>
            <a href="/panel-supervision.html" className="mobile-nav-item" role="menuitem" onClick={() => setShowMobileNav(false)}>
              <span aria-hidden="true">⚙</span>
              Ajustes
            </a>
          </div>
        )}
        <div className="school-card" aria-label="Escuela y ciclo escolar">
          <svg className="school-logo" viewBox="0 0 48 48" aria-hidden="true">
            <path d="M8 8h32v17c0 8.8-6.6 14.2-16 18-9.4-3.8-16-9.2-16-18V8Z" />
            <path d="M14 14h20v10c0 5.8-4.2 9.2-10 11.8-5.8-2.6-10-6-10-11.8V14Z" />
            <path d="M24 7v6" />
            <path d="M17 33c3.6-4 10.4-4 14 0" />
            <path d="M24 19l1.7 3.5 3.8.5-2.8 2.7.7 3.8-3.4-1.8-3.4 1.8.7-3.8-2.8-2.7 3.8-.5L24 19Z" />
          </svg>
          <div className="school-copy">
            <strong>Colegio del Valle</strong>
            <span>Ciclo 26-27</span>
          </div>
          <span className="school-caret" aria-hidden="true">⌄</span>
        </div>
      </aside>

      <main className="main-workspace" id="chat">
        <header className="top-bar">
          <div className="header-profile">
            <p className="eyebrow">Demo educativa</p>
            <div className="profile-select-shell">
              <span className="profile-select-icon">
                <ProfileIcon profile={selectedProfile} />
              </span>
              <label className="visually-hidden" htmlFor="profile-select">Perfil educativo</label>
              <select
                id="profile-select"
                value={selectedProfile}
                onChange={handleProfileChange}
                disabled={isLoading}
              >
                {PROFILE_OPTIONS.map((profile) => (
                  <option key={profile.value} value={profile.value}>
                    {profile.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="header-actions">
            <button
              type="button"
              className="new-conversation-button"
              onClick={handleNewConversation}
              disabled={isLoading}
            >
              <span aria-hidden="true">+</span>
              Nueva conversación
            </button>
            <button type="button" className="student-menu-button" aria-label={`Perfil del alumno: ${activeProfile.studentLabel}`}>
              <StudentAvatar profile={selectedProfile} />
              <span aria-hidden="true">⌄</span>
            </button>
          </div>
        </header>

        <div className="content-grid">
          <section className="chat-card" aria-label="Chat de LuxIA">
            <div className="chat-header">
              <div className="profile-pill">
                <img src={activeProfile.avatarSrc} alt="" />
                <span>{activeProfile.shortLabel}</span>
              </div>
            </div>

            <section className="messages-panel">
              {messages.length === 0 ? (
                <div className="empty-state">
                  <p className="empty-state__title">Inicia la conversación escribiendo tu primera pregunta.</p>
                  <div className="scenario-row" aria-label="Escenarios de demo">
                    <span>Foto de tarea</span>
                    <span>Práctica en inglés</span>
                    <span>Texto difícil</span>
                  </div>
                </div>
              ) : (
                messages.map((message, index) => (
                  <article
                    key={`${message.role}-${index}`}
                    className={`message-row ${message.role === 'user' ? 'student' : 'system'}`}
                  >
                    {message.role === 'assistant' && (
                      <img
                        src={activeProfile.avatarSrc}
                        alt="Avatar de LuxIA"
                        className="luxia-avatar"
                      />
                    )}
                    <div className={`message-bubble ${message.role === 'user' ? 'student' : 'system'}`}>
                      <p className="message-role">
                        {message.role === 'user' ? activeProfile.studentLabel : 'LuxIA'}
                      </p>
                      {message.imageData && (
                        <img
                          src={message.imageData.previewUrl}
                          alt="Imagen enviada"
                          className="message-image"
                        />
                      )}
                      {message.content && <FormattedMessage text={message.content} />}
                      {!message.imageData && !message.content && <FormattedMessage text="" />}
                      {message.role === 'assistant' &&
                        index === messages.length - 1 &&
                        Array.isArray(message.quickActions) &&
                        message.quickActions.length > 0 && (
                          <div className="quick-actions" aria-label="Opciones rápidas">
                            {message.quickActions.map((action) => (
                              <button
                                key={action.value || action.label}
                                type="button"
                                className="quick-action-button"
                                onClick={() => handleQuickAction(action)}
                                disabled={isLoading || isConversationLocked}
                              >
                                {action.label}
                              </button>
                            ))}
                          </div>
                        )}
                      {showDebug && message.role === 'assistant' && message.debug && (
                        <pre className="debug-panel">
                          {JSON.stringify(message.debug, null, 2)}
                        </pre>
                      )}
                    </div>
                    {message.role === 'user' && <StudentAvatar profile={selectedProfile} />}
                  </article>
                ))
              )}
              {isLoading && <p className="loading-state">LuxIA está pensando...</p>}
              <div ref={messagesEndRef} />
            </section>

            {errorMessage && <p className="error-message">{errorMessage}</p>}
            {isConversationLocked && (
              <p className="hold-message">
                La conversación quedó cerrada por hoy. Puedes volver a practicar en 12 horas con una conversación nueva.
              </p>
            )}

            <form className="composer" onSubmit={handleSendMessage}>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageSelect}
              />
              <button
                type="button"
                className="attach-button"
                onClick={() => imageInputRef.current?.click()}
                disabled={isLoading || isConversationLocked}
                title="Adjuntar imagen"
                aria-label="Adjuntar imagen"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              </button>
              <div className="composer-input-area">
                {attachedImage && (
                  <div className="image-preview-container">
                    <img
                      src={attachedImage.previewUrl}
                      alt="Imagen adjunta"
                      className="image-preview-thumb"
                    />
                    <button
                      type="button"
                      className="image-preview-remove"
                      onClick={() => setAttachedImage(null)}
                      aria-label="Quitar imagen"
                    >
                      ✕
                    </button>
                  </div>
                )}
                <textarea
                  ref={composerRef}
                  placeholder={
                    isConversationLocked
                      ? 'Conversación cerrada por hoy'
                      : attachedImage
                      ? 'Describe la imagen o haz una pregunta...'
                      : 'Escribe tu mensaje...'
                  }
                  value={inputMessage}
                  onChange={(event) => setInputMessage(event.target.value)}
                  onKeyDown={handleComposerKeyDown}
                  disabled={isLoading || isConversationLocked}
                  rows={1}
                />
              </div>
              <button
                className="send-button"
                type="submit"
                aria-label="Enviar"
                disabled={isLoading || isConversationLocked || (!inputMessage.trim() && !attachedImage)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M21 3 10 14" />
                  <path d="m21 3-7 18-4-7-7-4 18-7Z" />
                </svg>
              </button>
            </form>
          </section>

          <aside className="guardrails-panel" aria-label="Guardrails activos">
            <div className="guardrails-heading">
              <h2>Guardrails activos</h2>
              <span className="guardrails-shield">
                <ShieldIcon />
              </span>
            </div>
            <div className="guardrail-list">
              {GUARDRAILS.map((guardrail) => (
                <div className="guardrail-item" key={guardrail.label}>
                  <span className="guardrail-icon">
                    <GuardrailIcon type={guardrail.type} />
                  </span>
                  <p>{guardrail.label}</p>
                  <span className="guardrail-check">
                    <CheckIcon />
                  </span>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

export default App;
